/**
 * The ten components — US 17.04. Small DOM factories over the token
 * stylesheet; panels compose these instead of hand-rolling elements.
 * Nothing here talks to the server or the bus — pure presentation.
 */

export type ButtonVariant = 'default' | 'primary' | 'destructive';

export function button(
  label: string,
  onClick: () => void,
  variant: ButtonVariant = 'default',
): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = `wca-btn${variant === 'default' ? '' : ` wca-btn-${variant}`}`;
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}

/** Modal shell; returns close() so callers control dismissal. */
export function modal(root: HTMLElement, content: HTMLElement): { close: () => void } {
  const backdrop = document.createElement('div');
  backdrop.className = 'wca-modal-backdrop';
  const box = document.createElement('div');
  box.className = 'wca-modal';
  box.appendChild(content);
  backdrop.appendChild(box);
  root.appendChild(backdrop);
  const close = () => backdrop.remove();
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) close();
  });
  return { close };
}

/**
 * Confirm prompt — destructive actions require explicit confirm (US 12.04);
 * no browser confirm(), so it works identically with touch and styling.
 * The SAFE option gets focus by default (mirrors the overflow doctrine).
 */
export function confirmPrompt(
  root: HTMLElement,
  message: string,
  confirmLabel: string,
  destructive = true,
): Promise<boolean> {
  return new Promise((resolve) => {
    const content = document.createElement('div');
    const p = document.createElement('p');
    p.textContent = message;
    const actions = document.createElement('div');
    actions.className = 'wca-modal-actions';
    const { close } = modal(root, content);
    const done = (v: boolean) => {
      close();
      resolve(v);
    };
    const cancel = button('Cancel', () => done(false));
    const ok = button(confirmLabel, () => done(true), destructive ? 'destructive' : 'primary');
    actions.append(cancel, ok);
    content.append(p, actions);
    cancel.focus();
  });
}

export type ToastKind = 'info' | 'danger' | 'celebrate';

/** The single toast implementation — every panel routes through here. */
export function toast(root: HTMLElement, text: string, kind: ToastKind = 'info'): void {
  const el = document.createElement('div');
  el.className =
    kind === 'danger'
      ? 'wca-toast wca-toast-danger'
      : kind === 'celebrate'
        ? 'wca-toast wca-toast-celebrate'
        : 'wca-toast';
  el.textContent = text;
  root.appendChild(el);
  setTimeout(() => el.remove(), kind === 'celebrate' ? 4500 : 3500);
}

/** Persistent condition banner (connection lost, cutover warning). */
export function banner(
  root: HTMLElement,
  text: string,
  kind: 'danger' | 'warn',
): { remove: () => void } {
  const el = document.createElement('div');
  el.className = `wca-banner wca-banner-${kind}`;
  el.textContent = text;
  root.appendChild(el);
  return { remove: () => el.remove() };
}

/** Tooltip that works on hover AND on tap (US 12.04). */
export function attachTooltip(host: HTMLElement, text: string): void {
  host.classList.add('wca-tooltip-host');
  const tip = document.createElement('span');
  tip.className = 'wca-tooltip';
  tip.textContent = text;
  host.appendChild(tip);
  host.addEventListener('click', () => {
    host.classList.toggle('wca-tooltip-open');
    setTimeout(() => host.classList.remove('wca-tooltip-open'), 2500);
  });
}

export function progressBar(value: number, max: number, lowAt = 0.25): HTMLDivElement {
  const bar = document.createElement('div');
  const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  bar.className = `wca-progress${ratio <= lowAt ? ' wca-progress-low' : ''}`;
  const fill = document.createElement('div');
  fill.style.width = `${ratio * 100}%`;
  bar.appendChild(fill);
  return bar;
}

export function tabs(
  labels: string[],
  onSelect: (index: number) => void,
  selected = 0,
): HTMLDivElement {
  const bar = document.createElement('div');
  bar.className = 'wca-tabs';
  labels.forEach((label, i) => {
    const t = document.createElement('button');
    t.className = 'wca-tab';
    t.textContent = label;
    t.setAttribute('aria-selected', String(i === selected));
    t.addEventListener('click', () => {
      for (const other of bar.children) other.setAttribute('aria-selected', 'false');
      t.setAttribute('aria-selected', 'true');
      onSelect(i);
    });
    bar.appendChild(t);
  });
  return bar;
}

export function listRow(primary: string, secondary?: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'wca-listrow';
  const main = document.createElement('span');
  main.textContent = primary;
  row.appendChild(main);
  if (secondary) {
    const sub = document.createElement('small');
    sub.textContent = secondary;
    row.appendChild(sub);
  }
  return row;
}

/** ItemSlot — the inventory cell, tokens-styled via .wca-slot. */
export function itemSlot(label: string, qty: number, onTap: () => void): HTMLButtonElement {
  const cell = document.createElement('button');
  cell.className = 'wca-slot';
  cell.textContent = label;
  const q = document.createElement('span');
  q.className = 'wca-qty';
  q.textContent = `×${qty}`;
  cell.appendChild(q);
  cell.addEventListener('click', onTap);
  return cell;
}

/** PictureCard — rarity-framed via the seven rarity tokens. */
export function pictureCard(title: string, rarity: string): HTMLDivElement {
  const card = document.createElement('div');
  card.className = 'wca-picturecard';
  card.style.borderColor = `var(--wca-rarity-${rarity}, var(--wca-rarity-common))`;
  card.textContent = title;
  return card;
}
